import React, { useState, useEffect } from 'react';
import { Award, ExternalLink, AlertCircle, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Player avatar with fallback chain: photo -> school logo -> initials
const PlayerAvatar = ({ player }) => {
  const [imgError, setImgError] = useState(false);
  
  // If photo exists and hasn't errored, show it
  if (player.photo_url && !imgError && player.photo_type !== 'initials') {
    return (
      <img 
        src={player.photo_url}
        alt={player.player_name}
        className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-yellow-400/50"
        onError={() => setImgError(true)}
        data-testid={`player-avatar-${player.player_name.toLowerCase().replace(/\s+/g, '-')}`}
      />
    );
  }
  
  // Fallback to school logo
  if (player.school_logo && (imgError || !player.photo_url)) {
    return (
      <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/30">
        <img 
          src={player.school_logo}
          alt={player.school}
          className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
        />
      </div>
    );
  }
  
  // Final fallback: initials
  return (
    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center border-2 border-white/30">
      <span className="text-white font-bold text-lg sm:text-xl">
        {player.player_initials || player.player_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
      </span>
    </div>
  );
};

// Single award card
const AwardCard = ({ player, index }) => (
  <div 
    className="bg-gradient-to-br from-yellow-600/20 to-orange-600/20 backdrop-blur-sm rounded-xl p-4 border border-yellow-500/30"
    data-testid={`award-card-${index}`}
  >
    <div className="flex items-start gap-4">
      <PlayerAvatar player={player} />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Award className="w-4 h-4 text-yellow-400 flex-shrink-0" />
          <span className="text-yellow-400 text-xs font-bold uppercase truncate">
            {player.is_co_winner ? `Co-${player.award}` : player.award}
          </span>
        </div>
        <h4 className="text-white font-bold text-lg truncate">{player.player_name}</h4>
        <p className="text-white/70 text-sm">{player.school}</p>
        {player.stats_summary && (
          <p className="text-white/60 text-xs mt-2 line-clamp-2">{player.stats_summary}</p>
        )}
      </div>
    </div>
  </div>
);

// Main component
const SWACWeeklyAwards = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/swac-awards`);
        if (!response.ok) throw new Error('Failed to fetch awards');
        const result = await response.json();
        setData(result);
      } catch (err) {
        console.error('Error fetching awards:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAwards();
  }, []);
  
  if (loading) {
    return (
      <div className="bg-[#00204E]/50 backdrop-blur-sm rounded-xl p-6" data-testid="awards-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/20 rounded w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="h-32 bg-white/10 rounded-xl" />
            <div className="h-32 bg-white/10 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }
  
  if (error || !data?.awards?.length) {
    return (
      <div className="bg-[#00204E]/50 backdrop-blur-sm rounded-xl p-6" data-testid="awards-error">
        <div className="flex items-center gap-2 text-yellow-400 mb-2">
          <AlertCircle className="w-5 h-5" />
          <span className="font-bold">Awards Update Pending</span>
        </div>
        <p className="text-white/60 text-sm">
          Waiting for the latest SWAC weekly awards announcement from swac.org
        </p>
      </div>
    );
  }
  
  return (
    <div data-testid="swac-weekly-awards">
      {/* Header with source attribution */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Award className="w-6 h-6 text-yellow-400" />
          <h2 className="text-xl font-black text-white uppercase">SWAC Players of the Week</h2>
        </div>
        
        {data.source_url && (
          <a 
            href={data.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-white/60 hover:text-white/80 transition-colors"
            data-testid="source-link"
          >
            <ExternalLink className="w-3 h-3" />
            Source: SWAC
          </a>
        )}
      </div>
      
      {/* Announcement date */}
      {data.announcement_date && (
        <p className="text-white/50 text-xs mb-4">
          Announced: {new Date(data.announcement_date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </p>
      )}
      
      {/* Awards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.awards.map((player, idx) => (
          <AwardCard key={`${player.player_name}-${idx}`} player={player} index={idx} />
        ))}
      </div>
      
      {/* Last update info */}
      {data.last_update && (
        <p className="text-white/40 text-[10px] mt-4 text-right">
          Last verified: {new Date(data.last_update).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default SWACWeeklyAwards;
